import type { RunVisualArtifact } from "@/api/run-visuals";
import { isStructureBearingType } from "@/components/viz/molecule-structure";

/**
 * WHICH structures of a run the 3D view can be pointed at, and what each one
 * is called (flowstack#1320).
 *
 * The workspace used to resolve exactly one — `pickSubjectArtifact`, "the
 * molecule this run is about" — and a fragmented run has more than one. The
 * tyk2 golden run cuts two fragments and `jnk1_18658` cuts three, so a run with
 * several structures is the NORMAL case on this portal; what the viewer showed
 * was one of them, with nothing on screen saying which or that the others
 * existed.
 *
 * This is that set, as an ordered list a picker can render:
 *
 * 1. **The run's own ligand first**, and it stays the default. The first
 *    `Molecule` an AFFDO run *produces* is a fragment of the ligand, which is
 *    why `origin` exists at all (`api/run-visuals.ts`) — promoting a fragment
 *    to the default subject would be that same quietly-wrong render arrived at
 *    from the other side.
 * 2. **Then every fragment the run cut**, named by the `fragment_id` the
 *    server joined from the producing run's own `FragmentSet`
 *    (`run_cohort.fragment_ids_by_hash`). That is the same vocabulary the
 *    Analysis tab's fragment table and the torsion axes use, so a reader can
 *    cross-reference the two surfaces by the id rather than by position.
 *
 * **Nothing here is derived from a count.** The list is however long the run's
 * fragment set is; a two-fragment run and a three-fragment one differ only in
 * its length, and a run with none is a one-entry list rather than a special
 * case (which is what keeps "say which structure is on screen" true at n=1,
 * where the ambiguity the operator reported also exists).
 *
 * Pure over the visuals index, so the ordering and the fallbacks are pinnable
 * without mounting a viewer.
 */

/** What the picker calls the run's own ligand. */
export const LIGAND_SUBJECT_KEY = "ligand";

/**
 * ...and what it calls it in the one case that word is already taken.
 *
 * A `fragment_id` is a producer-assigned `IdString`, so nothing stops a
 * FragmentSet from naming a fragment `ligand`. Two identically-labelled
 * entries in a single-choice picker is a control that cannot say which one is
 * selected, so the ligand yields the plain word and takes this instead. It is
 * a collision guard, not an expected label — every real AFFDO run uses
 * `frag-<n>`.
 */
export const LIGAND_SUBJECT_FALLBACK_KEY = "ligand (whole molecule)";

export interface MoleculeSubject {
  /**
   * The fragment this subject IS, or `null` for the run's whole ligand.
   *
   * This — never {@link MoleculeSubject.key} — is what the workspace's shared
   * fragment selection stores, so the molecule view, the fit numbers and the
   * torsion plots all name the same fragment.
   */
  fragmentId: string | null;
  /** The label the picker renders, unique within the list. */
  key: string;
  artifact: RunVisualArtifact;
}

/**
 * Natural order over fragment ids — `frag-2` before `frag-10`.
 *
 * The index's own order cannot be used: it is `(created_at, content_hash)`,
 * and a fanout's fragments are registered inside one transaction, so the
 * tie-break is a hash and the picker's order would be arbitrary — stable
 * across polls, but arbitrary, which on a three-fragment run reads as a bug.
 * Lexicographic order is not it either: it puts `frag-10` between `frag-1` and
 * `frag-2`, which is invisible at today's counts and wrong the first time a
 * ligand cuts ten fragments.
 *
 * Digit runs compare as numbers and everything else as text, so ids that share
 * no shape still order deterministically rather than throwing.
 */
export function compareFragmentIds(a: string, b: string): number {
  const chunks = (value: string): string[] => value.match(/\d+|\D+/g) ?? [];
  const left = chunks(a);
  const right = chunks(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const l = left[i];
    const r = right[i];
    if (l === undefined) return -1;
    if (r === undefined) return 1;
    const bothNumeric = /^\d+$/.test(l) && /^\d+$/.test(r);
    const cmp = bothNumeric ? Number(l) - Number(r) : l.localeCompare(r);
    if (cmp !== 0) return cmp;
  }
  return 0;
}

/** The run's selectable structures, ligand first — see the module docstring. */
export function moleculeSubjects(artifacts: readonly RunVisualArtifact[]): MoleculeSubject[] {
  const structural = artifacts.filter((a) => isStructureBearingType(a.typed_object));

  // First artifact per fragment id wins, so a re-run that superseded a
  // fragment's structure cannot put the same id in the picker twice.
  const byFragment = new Map<string, RunVisualArtifact>();
  for (const artifact of structural) {
    const id = artifact.fragment_id;
    if (typeof id === "string" && id !== "" && !byFragment.has(id)) byFragment.set(id, artifact);
  }
  const fragmentIds = [...byFragment.keys()].sort(compareFragmentIds);

  // The ligand is what the run CONSUMED from outside itself. The produced
  // fallback is `pickSubjectArtifact`'s and is kept for the run that has no
  // input row (a generator Tile, or a flow whose input row a retention sweep
  // removed) — but it now skips anything we have identified as a fragment,
  // because a fragment appearing as "the ligand" is the exact substitution
  // `origin` exists to prevent, and until #1320 there was no way to tell.
  const ligand =
    structural.find((a) => a.origin === "input") ??
    structural.find((a) => a.origin === "produced" && !isFragment(a, byFragment)) ??
    null;

  const ligandKey = fragmentIds.includes(LIGAND_SUBJECT_KEY)
    ? LIGAND_SUBJECT_FALLBACK_KEY
    : LIGAND_SUBJECT_KEY;

  const subjects: MoleculeSubject[] = [];
  if (ligand !== null) subjects.push({ fragmentId: null, key: ligandKey, artifact: ligand });
  for (const id of fragmentIds) {
    const artifact = byFragment.get(id);
    if (artifact !== undefined) subjects.push({ fragmentId: id, key: id, artifact });
  }
  return subjects;
}

function isFragment(
  artifact: RunVisualArtifact,
  byFragment: ReadonlyMap<string, RunVisualArtifact>
): boolean {
  const id = artifact.fragment_id;
  return typeof id === "string" && byFragment.get(id)?.content_hash === artifact.content_hash;
}

/**
 * Which subject a fragment pick resolves to.
 *
 * Reconciled against the CURRENT list rather than merely remembered, for the
 * reason `resolveFragmentId` and `resolveSelectedView` both are: the list grows
 * while a run is live and a resume can supersede a fragment, so a remembered id
 * can name a structure that is no longer there. It falls back to the head of
 * the list — the ligand, where there is one — so a stale pick can never strand
 * the panel on nothing.
 *
 * `null` only when the run has no renderable structure at all.
 */
export function resolveMoleculeSubject(
  subjects: readonly MoleculeSubject[],
  fragmentId: string | null
): MoleculeSubject | null {
  if (fragmentId !== null) {
    const picked = subjects.find((s) => s.fragmentId === fragmentId);
    if (picked !== undefined) return picked;
  }
  return subjects[0] ?? null;
}

/**
 * The fragment id behind a picker label, for the `onChange` direction.
 *
 * `null` for the ligand entry AND for a label that is not in the list — the
 * caller's remedy is the same in both cases (fall back to the run's subject),
 * and inventing a fragment id from an unknown string is exactly what this
 * module exists to make impossible.
 */
export function subjectKeyToFragmentId(
  subjects: readonly MoleculeSubject[],
  key: string
): string | null {
  return subjects.find((s) => s.key === key)?.fragmentId ?? null;
}
